import { useBehavior, useButton, useDetectDocumentScroll } from '@libs/hooks';
import { AssetsSvgMap, TAssetsSvgMapValue, TButtonData, TCommonOption, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, MouseEvent, MouseEventHandler } from 'react';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { TButtonOption } from '../../types/options';
import { capitalText, getInlineStyles } from '../../utils/styles';
import { FlagIcon } from '../UI/FlagIcon/FlagIcon';
import styles from './styles/buttonPill.module.css';
import { getBackground, getBoxShadow, getColor, getContainerMarginLeft, getContainerMarginRight, getPaddingPostfix } from './styles/buttonPillUtils';

type TButtonPillProps<TD, TO> = (
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      }
    | ({
          isWidget: false;
          handleClick: MouseEventHandler;
          uid?: string;
          label?: string;
          dynamicShadow?: boolean;
          position?: 'floating' | 'inline';
          size?: 'large' | 'medium' | 'small';
          viewBackground?: 'plain' | 'gradient';
          fillContainer?: boolean;
          maxWidth?: string;
          iconName?: string;
          flagIcon?: string;
          iconPosition?: 'left' | 'right';
      } & Pick<TCommonOption, 'isDisabled'>)
) & {
    hierarchy?: 'primary' | 'secondary' | 'tertiary';
    customBackground?: string;
};

export const ButtonPill = (props: TButtonPillProps<TButtonData, TButtonOption>) => {
    const { isWidget } = props;
    const finalWidget = !isWidget
        ? ({
              uid: '',
              data: {
                  iconName: props.iconName,
                  flagIcon: props.flagIcon,
                  label: props.label,
              },
              options: {
                  isDisabled: props.isDisabled,
                  fillContainer: props.fillContainer,
                  size: props.size,
                  hierarchy: props.hierarchy,
                  viewBackground: props.viewBackground,
                  iconPosition: props.iconPosition,
                  dynamicShadow: props.dynamicShadow,
                  position: props.position,
              },
          } as TWidget<TButtonData, TButtonOption>)
        : props.widget;
    const { handleClick } = useButton(finalWidget);
    const { isDisabled, isHidden } = useBehavior(finalWidget);
    const { documentScrolled } = useDetectDocumentScroll(finalWidget.uid);
    const hierarchy = finalWidget.options?.hierarchy;
    const viewBackground = finalWidget.options?.viewBackground;
    const iconName = finalWidget.data?.iconName;
    const flagIcon = finalWidget.data?.flagIcon;
    const buttonSize = finalWidget.options?.size;
    const iconPosition = finalWidget.options?.iconPosition;
    const fillContainer = finalWidget.options?.fillContainer;

    const getPosition = () => {
        const dynamicShadow = finalWidget.options?.dynamicShadow;
        const dynamicShadowDefault = dynamicShadow ?? true;
        if (dynamicShadowDefault && documentScrolled && !isDisabled) {
            return 'floating';
        } else {
            return finalWidget.options?.position;
        }
    };

    const getFill = () => {
        if (hierarchy === 'tertiary' && viewBackground === 'gradient') return 'white_base';
        return 'black';
    };

    const buttonClass = clsx(
        styles.commonButton,
        fillContainer ? styles.fillContainer : styles.regularContainer,
        styles[`fontSize${capitalText(buttonSize)}`],
        styles[`color${capitalText(getColor(hierarchy, viewBackground, isDisabled))}`],
        styles[`background${capitalText(getBackground(hierarchy, viewBackground, isDisabled))}`],
        styles[`padding${capitalText(getPaddingPostfix(!!(iconName || flagIcon), iconPosition, buttonSize))}`],
        styles[`boxShadow${capitalText(getBoxShadow(getPosition(), hierarchy, isDisabled))}`],
        isDisabled && styles.disabled,
    );
    const iconContainerClass = clsx(
        styles.iconContainer,
        styles[`containerMarginRight${getContainerMarginRight(buttonSize, iconPosition)}`],
        styles[`containerMarginLeft${getContainerMarginLeft(buttonSize, iconPosition)}`],
        iconPosition === 'right' && styles.rightOrder,
    );

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <button
                data-test={finalWidget.uid}
                aria-disabled={isDisabled}
                aria-label={finalWidget.data?.label}
                aria-hidden={isHidden}
                disabled={isDisabled}
                className={buttonClass}
                style={getInlineStyles({
                    margin: finalWidget.options?.margin,
                    background: props.customBackground,
                    variables: { customMaxWidth: !isWidget && props.maxWidth ? props.maxWidth : 'calc(((100vw - 168px) / 6) * 2 + 24px)' },
                })}
                onClick={(e: MouseEvent) => {
                    if (isWidget) {
                        handleClick();
                    } else {
                        props.handleClick(e);
                    }
                }}
            >
                {iconName && (
                    <div className={iconContainerClass}>
                        <SvgComponent
                            width={buttonSize === 'small' ? 16 : 24}
                            height={buttonSize === 'small' ? 16 : 24}
                            name={iconName as TAssetsSvgMapValue}
                            defaultName={AssetsSvgMap.ic_x}
                            fill={getFill()}
                        />
                    </div>
                )}
                {flagIcon && (
                    <div className={iconContainerClass}>
                        <FlagIcon name={flagIcon} />
                    </div>
                )}
                <span className={styles.truncateText}>{finalWidget.data?.label}</span>
            </button>
        </Activity>
    );
};
